import { computed, ref, type Ref } from 'vue';

export type MergedPaginatedFeedPageParams = {
  limit: number;
  offset: number;
};

export type MergedPaginatedFeedSource<TItem> = {
  id: string;
  fetchPage: (params: MergedPaginatedFeedPageParams) => Promise<TItem[]>;
};

export type MergedPaginatedFeedComparator<TItem> = (left: TItem, right: TItem) => number;

export type UseMergedPaginatedFeedOptions<TItem> = {
  sources: () => MergedPaginatedFeedSource<TItem>[];
  pageSize: number;
  sourcePageSize?: number;
  compareItems: MergedPaginatedFeedComparator<TItem>;
  getItemKey: (item: TItem) => string;
};

type FeedStatus = 'idle' | 'loading-initial' | 'loading-more';

type SourceState<TItem> = {
  source: MergedPaginatedFeedSource<TItem>;
  offset: number;
  buffer: TItem[];
  isExhausted: boolean;
  isFetching: boolean;
};

type FeedState<TItem> = {
  rows: TItem[];
  sourceStates: SourceState<TItem>[];
  status: FeedStatus;
  error: unknown | undefined;
  requestVersion: number;
};

type SourcePageFetchResult = { status: 'fulfilled' } | { status: 'rejected'; error: unknown };

const orderItems = <TItem>(
  items: TItem[],
  compareItems: MergedPaginatedFeedComparator<TItem>,
): TItem[] => {
  return [...items].sort(compareItems);
};

const dedupeAndOrder = <TItem>(
  items: TItem[],
  getItemKey: (item: TItem) => string,
  compareItems: MergedPaginatedFeedComparator<TItem>,
): TItem[] => {
  const keyedItems = new Map<string, TItem>();

  for (const item of items) {
    keyedItems.set(getItemKey(item), item);
  }

  return orderItems([...keyedItems.values()], compareItems);
};

const createSourceState = <TItem>(
  source: MergedPaginatedFeedSource<TItem>,
): SourceState<TItem> => ({
  source,
  offset: 0,
  buffer: [],
  isExhausted: false,
  isFetching: false,
});

const createFeedState = <TItem>(
  requestVersion: number,
  sourceStates: SourceState<TItem>[] = [],
): FeedState<TItem> => {
  return {
    rows: [],
    sourceStates,
    status: 'idle',
    error: undefined,
    requestVersion,
  };
};

export const useMergedPaginatedFeed = <TItem>({
  sources,
  pageSize,
  sourcePageSize = pageSize,
  compareItems,
  getItemKey,
}: UseMergedPaginatedFeedOptions<TItem>) => {
  const feedState = ref(createFeedState<TItem>(0)) as Ref<FeedState<TItem>>;
  const rows = computed(() => feedState.value.rows);
  const isLoading = computed(() => feedState.value.status === 'loading-initial');
  const isLoadingMore = computed(() => feedState.value.status === 'loading-more');
  const error = computed(() => feedState.value.error);

  const isCurrentRequest = (version: number) => {
    return version === feedState.value.requestVersion;
  };

  const hasMore = computed(() => {
    return feedState.value.sourceStates.some(
      (state) => state.buffer.length > 0 || (!state.isExhausted && !state.isFetching),
    );
  });

  const reset = () => {
    feedState.value = createFeedState<TItem>(feedState.value.requestVersion + 1);
  };

  const fetchSourcePage = async (
    state: SourceState<TItem>,
    version: number,
  ): Promise<SourcePageFetchResult> => {
    state.isFetching = true;

    try {
      const items = await state.source.fetchPage({
        limit: sourcePageSize,
        offset: state.offset,
      });

      if (!isCurrentRequest(version)) {
        return { status: 'fulfilled' };
      }

      state.offset += items.length;
      state.buffer = dedupeAndOrder([...state.buffer, ...items], getItemKey, compareItems);
      state.isExhausted = items.length < sourcePageSize;
      return { status: 'fulfilled' };
    } catch (sourceError: unknown) {
      if (isCurrentRequest(version)) {
        state.isExhausted = true;
      }

      return { status: 'rejected', error: sourceError };
    } finally {
      if (isCurrentRequest(version)) {
        state.isFetching = false;
      }
    }
  };

  const fillEmptySourceBuffers = async (version: number) => {
    const statesToFetch = feedState.value.sourceStates.filter((state) => {
      return state.buffer.length === 0 && !state.isExhausted && !state.isFetching;
    });

    const fetchResults = await Promise.all(
      statesToFetch.map((state) => fetchSourcePage(state, version)),
    );

    if (!isCurrentRequest(version)) {
      return;
    }

    for (const fetchResult of fetchResults) {
      if (fetchResult.status === 'rejected') {
        feedState.value.error = fetchResult.error;
        return;
      }
    }
  };

  const getNextSourceState = () => {
    return feedState.value.sourceStates
      .filter((state) => state.buffer.length > 0)
      .sort((left, right) => {
        const [leftItem] = left.buffer;
        const [rightItem] = right.buffer;

        if (leftItem === undefined || rightItem === undefined) {
          return 0;
        }

        return compareItems(leftItem, rightItem);
      })[0];
  };

  const loadNextPage = async ({ initial }: { initial: boolean }) => {
    if (feedState.value.status !== 'idle') {
      return;
    }

    const version = feedState.value.requestVersion;
    feedState.value.error = undefined;
    feedState.value.status = initial ? 'loading-initial' : 'loading-more';

    try {
      const nextRows: TItem[] = [];

      while (nextRows.length < pageSize) {
        await fillEmptySourceBuffers(version);

        if (!isCurrentRequest(version)) {
          return;
        }

        const nextSourceState = getNextSourceState();

        if (nextSourceState === undefined) {
          break;
        }

        const nextItem = nextSourceState.buffer.shift();

        if (nextItem !== undefined) {
          nextRows.push(nextItem);
        }
      }

      feedState.value.rows = dedupeAndOrder(
        [...feedState.value.rows, ...nextRows],
        getItemKey,
        compareItems,
      );
    } catch (loadError: unknown) {
      if (isCurrentRequest(version)) {
        feedState.value.error = loadError;
      }
    } finally {
      if (isCurrentRequest(version)) {
        feedState.value.status = 'idle';
      }
    }
  };

  const loadInitial = async () => {
    feedState.value = createFeedState<TItem>(
      feedState.value.requestVersion + 1,
      sources().map(createSourceState),
    );

    await loadNextPage({ initial: true });
  };

  const loadMore = async () => {
    await loadNextPage({ initial: false });
  };

  return {
    rows,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadInitial,
    loadMore,
    reset,
  };
};
